import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WalletPlatformStatus } from '@cloudsign/shared';
import type { WalletCredentialsUpdate, WalletTenantSettings } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Alert, Badge, Button, Card, CheckboxField, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { useScopedSettings } from '../sites/api';

/** Tenant wallet card: who gets an "Add to Google Wallet" button, and the card title. Normal tenant settings route. */
export function WalletTenantCard() {
  const settings = useScopedSettings('/settings', 'wallet');
  const [draft, setDraft] = useState<Partial<WalletTenantSettings>>({});
  if (settings.query.isError) return <ErrorPanel error={settings.query.error} onRetry={() => void settings.query.refetch()} />;
  const saved = settings.values as Partial<WalletTenantSettings> | undefined;
  if (!saved) return <Spinner />;
  const v = { forVisitors: true, forMembers: true, cardTitle: '', ...saved, ...draft };
  const changed = Object.keys(draft).length > 0;
  const errs = fieldErrors(settings.save.error);
  return (
    <Card
      title={t('setup.wallet.tenant.title')}
      description={t('setup.wallet.tenant.description')}
      footer={
        <>
          <Button disabled={!changed} onClick={() => setDraft({})}>
            {t('settings.discard')}
          </Button>
          <Button variant="primary" disabled={!changed || settings.save.isPending} onClick={() => settings.save.mutate(draft, { onSuccess: () => setDraft({}) })}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorLine error={Object.keys(errs).length === 0 ? settings.save.error : null} />
        <CheckboxField label={t('setup.wallet.tenant.forVisitors')} hint={t('setup.wallet.tenant.forVisitorsHint')} checked={v.forVisitors} onChange={(e) => setDraft((d) => ({ ...d, forVisitors: e.target.checked }))} />
        <CheckboxField label={t('setup.wallet.tenant.forMembers')} hint={t('setup.wallet.tenant.forMembersHint')} checked={v.forMembers} onChange={(e) => setDraft((d) => ({ ...d, forMembers: e.target.checked }))} />
        <TextField label={t('setup.wallet.tenant.cardTitle')} hint={t('setup.wallet.tenant.cardTitleHint')} maxLength={60} value={v.cardTitle} onChange={(e) => setDraft((d) => ({ ...d, cardTitle: e.target.value }))} error={errs['cardTitle']} />
      </div>
    </Card>
  );
}

/** Tenant page "Wallet pass". Needs wallet.manage. */
export function WalletPage() {
  return (
    <>
      <PageHeader title={t('setup.wallet.title')} description={t('setup.wallet.description')} />
      <WalletTenantCard />
    </>
  );
}

const PLATFORM_KEY = ['wallet', 'platform', 'google'] as const;

/**
 * Platform settings tab "Google Wallet": the Google Wallet issuer. Uses the dedicated GET/PUT /wallet/platform/google route
 * (never the generic settings PATCH). The private key is write-only: it is sent when typed and never shown again.
 */
export function WalletPlatformCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: PLATFORM_KEY, queryFn: () => api.get('/wallet/platform/google', { schema: WalletPlatformStatus }) });
  const [draft, setDraft] = useState<WalletCredentialsUpdate>({});
  const save = useMutation({
    mutationFn: () => api.put('/wallet/platform/google', { body: draft, schema: WalletPlatformStatus }),
    onSuccess: (s) => {
      qc.setQueryData(PLATFORM_KEY, s);
      setDraft({});
    },
  });
  const errs = fieldErrors(save.error);
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const s = q.data;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <>
      {!s ? (
        <Spinner />
      ) : (
        <Card
          title={t('setup.wallet.platform.google')}
          description={t('setup.wallet.platform.googleHelp')}
          footer={
            <Button variant="primary" disabled={Object.keys(draft).length === 0 || save.isPending} onClick={() => save.mutate()}>
              {t('common.save')}
            </Button>
          }
        >
          <form onSubmit={submit} className="space-y-4">
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone={s.live ? 'good' : 'neutral'}>{s.live ? t('setup.wallet.platform.live') : t('setup.wallet.platform.notLive')}</Badge>
              <Badge tone={s.keySet ? 'good' : 'warn'}>{s.keySet ? t('setup.wallet.platform.keySet') : t('setup.wallet.platform.keyNotSet')}</Badge>
            </p>
            {(draft.provider ?? s.provider) === 'google' && !s.live && Object.keys(draft).length === 0 ? <Alert kind="info">{t('setup.wallet.platform.incomplete')}</Alert> : null}
            <ErrorLine error={Object.keys(errs).length === 0 ? save.error : null} />
            <SelectField label={t('setup.wallet.platform.provider')} hint={t('setup.wallet.platform.providerHint')} value={draft.provider ?? s.provider} onChange={(e) => setDraft((d) => ({ ...d, provider: e.target.value as WalletCredentialsUpdate['provider'] }))}>
              <option value="off">{t('setup.wallet.platform.provider.off')}</option>
              <option value="log">{t('setup.wallet.platform.provider.log')}</option>
              <option value="google">{t('setup.wallet.platform.provider.google')}</option>
            </SelectField>
            <TextField label={t('setup.wallet.platform.issuerId')} maxLength={40} value={draft.issuerId ?? s.issuerId} onChange={(e) => setDraft((d) => ({ ...d, issuerId: e.target.value }))} error={errs['issuerId']} autoComplete="off" />
            <TextField label={t('setup.wallet.platform.email')} type="email" maxLength={200} value={draft.serviceAccountEmail ?? s.serviceAccountEmail} onChange={(e) => setDraft((d) => ({ ...d, serviceAccountEmail: e.target.value }))} error={errs['serviceAccountEmail']} autoComplete="off" />
            <div>
              <label htmlFor="wallet-key" className="mb-1 block text-sm font-medium text-slate-800">
                {t('setup.wallet.platform.privateKey')}
              </label>
              <textarea
                id="wallet-key"
                rows={5}
                className="block w-full rounded-md border border-slate-300 px-3 py-1.5 font-mono text-sm"
                value={draft.privateKey ?? ''}
                placeholder={s.keySet ? t('setup.wallet.platform.keyPlaceholderSet') : ''}
                autoComplete="off"
                spellCheck={false}
                aria-describedby="wallet-key-hint"
                aria-invalid={errs['privateKey'] ? true : undefined}
                onChange={(e) => {
                  const value = e.target.value;
                  setDraft((d) => {
                    const { privateKey: _drop, ...rest } = d;
                    return value.trim() === '' ? rest : { ...rest, privateKey: value };
                  });
                }}
              />
              <p id="wallet-key-hint" className="mt-1 text-xs text-slate-600">
                {t('setup.wallet.platform.privateKeyHint')}
              </p>
              {errs['privateKey'] ? <p className="mt-1 text-sm text-red-700">{errs['privateKey']}</p> : null}
            </div>
          </form>
        </Card>
      )}
    </>
  );
}
